import React, { useState, useEffect } from "react";
import DevDataService from "../services/DevService";
import UserDataService from "../services/UserService";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import { formatWithCommas, formatToTwoDecimals } from "../../utils/formatNumber";
import DevMinWelSplitModal from "../../components/DevMinWelSplitModal";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const AddDev = () => {
  const { owner } = useParams();
  const navigate = useNavigate();
  const initialDevState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    creditOrDebit: "credit",
    description: "",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null
  };

  const [Dev, setDev] = useState(initialDevState);
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");
  const [splitModalOpen, setSplitModalOpen] = useState(false);

  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "amount") {
      setDev(prev => ({ ...prev, amount: formatWithCommas(value) }));
    } else {
      setDev(prev => ({ ...prev, [name]: value }));
    }
  };

  const saveDev = async () => {
    setError("");
    if (!Dev.amount || Number.isNaN(Number(String(Dev.amount).replace(/,/g, "")))) {
      setError("Please enter a valid amount");
      return;
    }
    if (!Dev.creditOrDebit) {
      setError("Please select transaction type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        owner: Dev.owner,
        date: typeof Dev.date === "string" ? Dev.date : toDateInput(Dev.date),
        amount: Number(String(Dev.amount).replace(/,/g, "")),
        creditOrDebit: String(Dev.creditOrDebit),
        description: (Dev.description || "").trim()
        // DO NOT send createdAt/createdBy from the client — backend must set createdBy from the authenticated user
      };

      const res = await DevDataService.create(Dev.owner, payload);
      const r = res?.data || {};

      setDev({
        owner: r.owner ?? Dev.owner,
        date: r.date ?? payload.date,
        amount: r.amount ?? payload.amount,
        creditOrDebit: r.creditOrDebit ?? payload.creditOrDebit,
        description: r.description ?? payload.description,
        createdAt: r.createdAt ?? null,
        createdBy: r.createdBy ?? null,
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null
      });

      // navigate back to the Dev list for the owner after successful save
      navigate(`/dashboard/Dev/${encodeURIComponent(r.owner ?? Dev.owner)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newDev = () => {
    setDev({ ...initialDevState, owner: owner || initialDevState.owner });
    setSubmitted(false);
    setError("");
    if (!owner) setOwnerDisplay("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2, md: 4 } }}>
      <Typography variant="h6" gutterBottom>
        {submitted ? "Transaction saved" : "Add Dev Transaction"}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newDev}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Owner"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={owner ? "Owner provided via route (read-only)" : "Owner (read-only)"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="date"
                name="date"
                label="Date of Deposit"
                helperText="Date deposit occurred (system creation timestamp is recorded automatically)"
                type="date"
                value={Dev.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="amount"
                name="amount"
                label="Amount"
                type="text"
                placeholder="e.g. 4,000,000.00"
                value={Dev.amount}
                onChange={handleInputChange}
                onBlur={() => {
                  if (Dev.amount) {
                    setDev(prev => ({ ...prev, amount: formatToTwoDecimals(prev.amount) }));
                  }
                }}
                fullWidth
                InputProps={{ inputMode: "numeric" }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">Transaction Type</Typography>
                <Button
                  variant={Dev.creditOrDebit === "credit" ? "contained" : "outlined"}
                  onClick={() => setDev(s => ({ ...s, creditOrDebit: "credit" }))}
                >
                  Credit
                </Button>
                <Button
                  variant={Dev.creditOrDebit === "debit" ? "contained" : "outlined"}
                 onClick={() => setDev(s => ({ ...s, creditOrDebit: "debit" }))}
                >
                  Debit
                </Button>
              </Stack>
            </Grid>

            {/* ONE-TIME DUES SPLIT TOOL (Welfare, Minutes, Development with 1x Stamp Duty) */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#F0FDF4",
                  border: "1px dashed #86EFAC",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VolunteerActivismRoundedIcon sx={{ color: "#0D9488" }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0F766E" }}>
                      Welfare, Minutes &amp; Development Split Tool
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#166534" }}>
                      One-time deposit split across Welfare, Minutes, and Development with unified Stamp Duty
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSplitModalOpen(true)}
                  startIcon={<CallSplitRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    borderColor: "#0D9488",
                    color: "#0D9488",
                    bgcolor: "#ffffff",
                    "&:hover": { bgcolor: "#E6FFFA", borderColor: "#0F766E" }
                  }}
                >
                  Open Dues Split Tool (1x Stamp Duty)
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description (optional)"
                value={Dev.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter a brief description of the transaction"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveDev}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                  Submit
                </Button>
                <Button variant="outlined" onClick={newDev} disabled={loading}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => window.history.back()}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Welfare, Minutes & Development Split Modal */}
      <DevMinWelSplitModal
        open={splitModalOpen}
        onClose={() => setSplitModalOpen(false)}
        initialMemberId={Dev.owner || owner}
        onSuccess={() => {
          setSnackOpen(true);
          const targetId = Dev.owner || owner;
          if (targetId) {
            navigate(`/dashboard/dev/${encodeURIComponent(targetId)}`);
          }
        }}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Transaction saved successfully
        </Alert>
      </Snackbar>
    </Paper>
  );
};
export default AddDev;
